import { ProfileFormValues, UserResponse } from '@/types/user';
import { Card, Grid, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { titleCase } from '@/utils/stringCaser';
import { SVForm, SVTextInput } from '@/components/core';
import ProfileEditAvatarButton from '../ProfileEditAvatarButton';
import ProfileEditSharedInputs from '../ProfileEditSharedInputs';
import { MESSAGES } from '@/constants/messages';
import { ROUTES } from '@/constants/routes';
import { useUpdateUser } from '@/hooks/requests/useUser';
import showToast from '@/utils/toast';
import { useNavigate } from 'react-router-dom';
import FormSubmissionButtons from '@/components/FormSubmissionButtons';

type ProfileEditStaffFormProps = {
  user: UserResponse;
};

export default function ProfileEditStaffForm({
  user,
}: ProfileEditStaffFormProps) {
  const navigate = useNavigate();
  const { updateStaff, isUpdateStaffPending } = useUpdateUser();
  const formMethods = useForm<ProfileFormValues>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      company: user.staffInfo?.companies
        .map(company => company.name)
        .join('\n'),
      role: titleCase(user.role),
      title: user.title,
      phone: user.phoneNumber,
      businessPhone: user.businessPhoneNumber,
      avatarUrl: user.avatarUrl || '',
      avatarDisplayName: '',
    },
    mode: 'onBlur',
  });

  async function onSubmit(data: ProfileFormValues) {
    try {
      await updateStaff(data);
      showToast({
        type: 'success',
        data: {
          content: 'Your profile has been successfully updated',
        },
      });
      navigate(ROUTES.PROFILE);
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }
  return (
    <SVForm<ProfileFormValues> methods={formMethods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Card>
          <Grid spacing={2} container>
            <Grid container size={{ xs: 12, md: 9 }}>
              <ProfileEditSharedInputs />
              <Grid size={{ xs: 12 }}>
                <SVTextInput
                  multiline
                  multilineRows={2}
                  name="bio"
                  label="Bio"
                  rules={{ maxLength: 1000 }}
                />
              </Grid>
            </Grid>
            <Grid sx={{ textAlign: 'center' }} size={{ xs: 12, md: 3 }}>
              <ProfileEditAvatarButton uuid={user.uuid} />
            </Grid>
          </Grid>
        </Card>
        <FormSubmissionButtons
          isLoading={isUpdateStaffPending}
          onLeave={() => navigate(ROUTES.PROFILE)}
        />
      </Stack>
    </SVForm>
  );
}
